import { isNotNil } from "ramda";
import { INumberInputProps, NumberInput } from "taltech-styleguide";
import { TTFormInputProps } from "./input.types";
import { InputBase } from "./InputBase";
import { useField } from "formik";

/**
 * Component: InputNumber
 *
 * This component renders an input field for numeric values using the NumberInput component.
 * It supports various configurations for decimal scale, negative values, and value range.
 *
 * Props:
 * @param {string} id - The id for the input field.
 * @param {number} decimalScale - The number of decimal places to show. Default is 0.
 * @param {boolean} allowNegative - Whether to allow negative values. Default is false.
 * @param {boolean} fixedDecimalScale - Whether to fix the decimal scale. Default is true.
 * @param {number} max - The maximum value allowed.
 * @param {function} isAllowed - Custom validation function to check if the value is allowed.
 * @param {number} minValue - The minimum value allowed. Default is 0.
 * @param {number} maxValue - The maximum value allowed. Default is Number.MAX_VALUE.
 * @param {any} props - Additional props to pass to the NumberInput component.
 *
 * Returns:
 * @returns {JSX.Element} A number input field with validation and formatting.
 */
export const InputNumber = ({
  id,
  decimalScale = 0,
  allowNegative = false,
  fixedDecimalScale = false,
  max,
  isAllowed,
  minValue = 0,
  maxValue = Number.MAX_VALUE,
  ...props
}: any) => {
  const [field, meta, helpers] = useField(id || props.field.name);
  const isAllowedValidator = (values: any) => {
    const { floatValue, value } = values;
    if (value === "") {
      return true;
    }
    return floatValue >= minValue && floatValue <= maxValue;
  };

  return (
    <NumberInput
      field={field}
      id={id}
      type="text"
      name={id}
      onValueChange={(evt) => {
        const { floatValue } = evt;
        helpers.setValue(floatValue);
      }}
      isAllowed={isNotNil(isAllowed) ? isAllowed : isAllowedValidator}
      onBlur={() => {
        if (!meta.touched) {
          helpers.setTouched(true);
        }
      }}
      allowedDecimalSeparators={[".", ","]}
      decimalScale={decimalScale}
      fixedDecimalScale={fixedDecimalScale}
      max={max}
      disabled={props.disabled}
      value={field.value}
      className={props.elementClass}
      suffix={props.suffix}
    />
  );
};

/**
 * Component: InputNumberFloat
 *
 * This component renders a floating-point number input field with a default decimal scale of 2.
 * It uses the InputNumber component with specific default properties.
 *
 * Props:
 * @param {TTFormInputProps} props - The props for the InputNumberFloat component.
 *
 * Returns:
 * @returns {JSX.Element} A floating-point number input field.
 */
export const InputNumberFloat = (
  props: TTFormInputProps & INumberInputProps & { maxValue: number }
) => {
  const newProps = {
    decimalScale: 2,
    component: InputNumber,
    max: 2000,
    ...props,
  };
  return <InputBase {...newProps} component={InputNumber} />;
};

/**
 * Component: InputNumberInteger
 *
 * This component renders an integer number input field.
 * It uses the InputNumber component without any specific decimal scale.
 *
 * Props:
 * @param {TTFormInputProps} props - The props for the InputNumberInteger component.
 *
 * Returns:
 * @returns {JSX.Element} An integer number input field.
 */

export const InputNumberInteger = (
  props: TTFormInputProps & { suffix?: string }
) => <InputBase {...props} component={InputNumber} />;
